import { useContext } from 'react'
import { useIntl } from 'react-intl'

import BookedOfferDetailsCTA from './BookedOfferDetailsCTA'
import LEOfferCTAs from './LEOfferCTAs'

import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import ViewMoreOffersCta from 'tripPlanner/components/Buttons/ViewMoreOffersCta'
import type { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import TripContext from 'tripPlanner/contexts/TripContext'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: AccommodationItem
  isOfferAvailable: boolean
  variant: DetailItemCardVariant
  offerLink?: string
  offer: App.HotelOffer | App.BedbankOffer | App.BundleOffer | App.VillaOffer
}

function HotelCtaContent(props: Props) {
  const intl = useIntl()
  const { isOfferAvailable, item, offerLink, offer } = props
  const { startDate: checkIn, endDate: checkOut, code: offerId, tripId } = item
  const canEdit = useCanEditTrip()
  const { viewOfferCopy } = useTripPlannerCTALabels()
  const tripContext = useContext(TripContext)
  const isPublicTrip = tripContext?.isPublicTrip
  const buttonSize = 'medium'

  if (isPublicTrip) {
    return (
      <Group direction="horizontal" gap={12}>
        <SaveTripItemButton item={item} />
        {offerLink && (
          <TextButton kind="primary" to={offerLink} target="_blank">
            {viewOfferCopy}
          </TextButton>
        )}
      </Group>
    )
  } else if (item.isBooked && item.confirmationCode) {
    return <BookedOfferDetailsCTA item={item} size={buttonSize} />
  } else if (!isOfferAvailable) {
    const hasPlannedInfo = !!checkIn || !!checkOut

    return (
      <Group
        direction="horizontal"
        gap={8}
        tabletHorizontalAlign="space-between"
        horizontalAlign="stretch"
      >
        {hasPlannedInfo && offerLink && (
          <TextButton
            kind="tertiary"
            to={addTripIdParam(offerLink, tripId, canEdit)}
            target="_blank"
            size={buttonSize}
          >
            {intl.formatMessage({
              id: '_stYoz6OrunuZqZygnTj',
              defaultMessage: 'Change details',
              description: 'Button to change booking details for a hotel trip item',
            })}
          </TextButton>
        )}
        <ViewMoreOffersCta
          offerId={offerId}
          checkIn={checkIn}
          checkOut={checkOut}
          sectionId={tripItemSelectionId(item.id)}
          tripId={item.tripId}
          showSearch={props.variant === 'tripSummary'}
          size={buttonSize}
        />
      </Group>
    )
  } else if (isLeBookmark(item) && offerLink) {
    return (
      <LEOfferCTAs
        item={item}
        variant={props.variant}
        offerLink={offerLink}
        hotelOffer={offer}
      />
    )
  } else {
    return null
  }
}

export default HotelCtaContent
